import React, { useMemo } from 'react';
import { ErrorMessage, Grid, Notification } from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { IVendorOrderInformationData } from '../../../types/vendorDashboard/IVendorOrderInformation';
import { IVendorDocuments, StatusSection } from '../../../components/vendorDashboard/StatusSection';
import { IStandardDocumentVersionData } from '../../../types/order/documents/shared/interfaces';
import { ITaxCertificationData } from '../../../ui-toolkit/taxCertifications/ITaxCertification';
import { OrderPanel } from '../../../components/vendorDashboard/OrderPanel';
import { OrderDetails } from '../../../components/vendorDashboard/OrderDetails';
import { Comments } from '../../../components/vendorDashboard/comments/Comments';
import { DocumentBlock } from '../../../components/vendorDashboard/DocumentBlock';
import { SearchReportBlock } from '../../../components/vendorDashboard/SearchReportBlock';
import { TaxCertificationBlock } from '../../../components/vendorDashboard/TaxCertificationBlock';
import { ClientUrlFactory } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { IDocumentCategory } from '../../../types/order/documents/IDocumentCategoryType';
import { eVendorServiceType } from '../../../types/order/title/eVendorServiceType';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../API/documentCategories/DocumentCategoriesApi';

export interface IVendorOrderPageProps {
  orderId: string;
  order: IVendorOrderInformationData;
  documents: IStandardDocumentVersionData[];
  requiredDocuments: IVendorDocuments[];
  optionalDocuments: IVendorDocuments[];
  pageError?: string;
  uploadNewCommentFetch: IFetchStatus;
  onSendToSpruce: () => void;
  sendToSpruceFetchStatus: IFetchStatus;
  sendToSpruceError?: string;
  taxCert?: ITaxCertificationData;
  onSendTaxCertToSpruce: () => void;
}

export const VendorOrderPage: React.FC<IVendorOrderPageProps> = ({
  orderId,
  order,
  pageError,
  documents,
  requiredDocuments,
  optionalDocuments,
  onSendToSpruce,
  sendToSpruceFetchStatus,
  sendToSpruceError,
  taxCert,
  onSendTaxCertToSpruce,
}) => {
  const documentCategoryFilter = (doc: IDocumentCategory) => {
    return !!doc.vendorTitleSearch;
  };

  const { data: { allCategories, documentCategoriesByName } = defaultNormalizedDocumentCategoriesData } =
    useListDocumentCategoriesQuery();

  const readyToSend = useMemo(() => {
    return requiredDocuments.every((doc) => doc.document) && !!order.titleCommitmentFinalizedAt;
  }, [requiredDocuments, order]);

  return (
    <>
      <OrderPanel
        prefix={'Title Search'}
        backTo={ClientUrlFactory().urlFor.vendorOrders()}
        fileNumber={order.fileNumber}
        createdAt={order.timeEnteredQueue}
        readyToSend={readyToSend}
        onSendToSpruce={onSendToSpruce}
        status={order.status}
        sendToSpruceFetchStatus={sendToSpruceFetchStatus}
      />
      {pageError && <ErrorMessage message={pageError} />}
      {sendToSpruceFetchStatus.success && !sendToSpruceError && (
        <Notification type={'success'} message={'Thank you! Spruce has been notified.'} />
      )}
      <Grid columnCount={3}>
        <OrderDetails order={order} />
        <StatusSection status={order.status} vendorDocuments={requiredDocuments.concat(optionalDocuments)} />
        <Comments orderId={orderId} vendorServiceType={eVendorServiceType.titleSearchRequest} />
      </Grid>
      <DocumentBlock
        documents={documents}
        documentCategoriesByName={documentCategoriesByName}
        documentCategories={allCategories}
        documentCategoryFilter={documentCategoryFilter}
        orderId={orderId}
      />
      <SearchReportBlock orderId={order.orderId} />
      <TaxCertificationBlock orderId={order.orderId} taxCert={taxCert} onSendTaxCertToSpruce={onSendTaxCertToSpruce} />
    </>
  );
};
